import React from 'react';
import { Card, CardContent } from './ui/card';
import { Badge } from './ui/badge';
import { Star, Quote, Sparkles, Heart, Award, Users } from 'lucide-react';
import { mockData } from './mock';
import { useScrollAnimation, createScrollAnimationClasses } from '../hooks/useScrollAnimation';

export const TestimonialsSection = () => {
  const [headerRef, headerVisible] = useScrollAnimation({ delay: 0 });
  const [testimonialsRef, testimonialsVisible] = useScrollAnimation({ delay: 200 });
  const [trustRef, trustVisible] = useScrollAnimation({ delay: 400 });

  return (
    <section className="py-24 bg-gradient-to-br from-gray-50 via-white to-blue-50/30 relative overflow-hidden">
      {/* Background Decorations */}
      <div className="absolute top-0 left-0 w-full h-full overflow-hidden pointer-events-none">
        <div className="absolute top-20 left-20 w-96 h-96 bg-blue-200/10 rounded-full blur-3xl"></div>
        <div className="absolute bottom-20 right-20 w-80 h-80 bg-purple-200/10 rounded-full blur-3xl"></div>
        <div className="absolute top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2 w-64 h-64 bg-green-200/10 rounded-full blur-3xl"></div>
      </div>

      <div className="container mx-auto px-4 relative z-10">
        {/* Enhanced Section Header */}
        <div ref={headerRef} className={`text-center mb-20 ${createScrollAnimationClasses('fadeInUp', headerVisible)}`}>
          <div className="inline-flex items-center gap-2 bg-gradient-to-r from-blue-100 to-indigo-100 px-6 py-3 rounded-full mb-6 border border-blue-200/50 backdrop-blur-sm">
            <Sparkles size={16} className="text-blue-600" />
            <Badge className="bg-transparent text-blue-700 px-0 py-0 text-sm font-semibold">
              Client Success Stories
            </Badge>
          </div>
          <h2 className="text-4xl lg:text-6xl font-bold text-gray-900 mb-6 leading-tight">
            Trusted by Leading
            <span 
              className="block bg-clip-text text-transparent"
              style={{ 
                backgroundImage: 'linear-gradient(to right, rgb(6 47 136), rgb(147 51 234))' 
              }}
            >
              Organizations
            </span>
          </h2>
          <p className="text-xl text-gray-600 max-w-4xl mx-auto leading-relaxed">
            See how our innovative traffic technology solutions are transforming 
            cities and improving lives across the Sultanate of Oman. Discover 
            the impact we're making together.
          </p>
        </div>

        {/* Enhanced Testimonials Grid */}
        <div ref={testimonialsRef} className={`grid grid-cols-1 md:grid-cols-2 gap-8 lg:gap-12 max-w-6xl mx-auto ${createScrollAnimationClasses('fadeInUp', testimonialsVisible)}`}>
          {mockData.testimonials.map((testimonial, index) => (
            <Card key={testimonial.id} className="group relative overflow-hidden border-0 shadow-xl hover:shadow-2xl transition-all duration-500 hover:-translate-y-3 bg-white/90 backdrop-blur-sm">
              {/* Enhanced Quote Icon */}
              <div className="absolute top-6 right-6 opacity-5 group-hover:opacity-10 transition-opacity duration-500">
                <Quote size={64} className="text-blue-600" />
              </div>

              {/* Floating Elements */}
              <div className="absolute top-4 left-4 opacity-0 group-hover:opacity-100 transition-opacity duration-500">
                {/* <div className="flex items-center gap-2 bg-blue-500/90 backdrop-blur-sm rounded-lg px-3 py-2">
                  <Heart size={14} className="text-white" />
                  <span className="text-white text-sm font-semibold">5-Star Review</span>
                </div> */}
              </div>

              <CardContent className="p-8 lg:p-10">
                {/* Enhanced Stars */}
                <div className="flex space-x-1 mb-6">
                  {[...Array(5)].map((_, i) => (
                    <Star 
                      key={i} 
                      size={18} 
                      className="text-yellow-400 fill-current group-hover:scale-110 transition-transform duration-300"
                      style={{ animationDelay: `${i * 100}ms` }}
                    />
                  ))}
                </div>

                {/* Enhanced Testimonial Content */}
                <blockquote className="text-gray-700 text-lg lg:text-xl leading-relaxed mb-8 relative z-10 group-hover:text-gray-800 transition-colors">
                  "{testimonial.content}"
                </blockquote>

                {/* Enhanced Author */}
                <div className="flex items-center group/author">
                  <div className="w-14 h-14 bg-gradient-to-br from-blue-600 to-indigo-600 rounded-2xl flex items-center justify-center text-white font-bold text-lg mr-4 group-hover:scale-110 transition-transform duration-300 shadow-lg">
                    {testimonial.avatar}
                  </div>
                  <div className="flex-1">
                    <div className="font-bold text-gray-900 text-lg group-hover:text-blue-600 transition-colors">
                      {testimonial.name}
                    </div>
                    <div className="text-sm text-gray-600 font-medium mb-1">
                      {testimonial.position}
                    </div>
                    <div className="text-sm text-blue-600 font-semibold group-hover:text-blue-700 transition-colors">
                      {testimonial.organization}
                    </div>
                  </div>
                  
                  {/* Floating Award Icon */}
                  <div className="opacity-0 group-hover:opacity-100 transition-opacity duration-500">
                    <div className="w-8 h-8 bg-gradient-to-r from-yellow-400 to-orange-400 rounded-full flex items-center justify-center">
                      <Award size={16} className="text-white" />
                    </div>
                  </div>
                </div>
              </CardContent>

              {/* Bottom Accent */}
              <div className="absolute bottom-0 left-0 w-full h-1 bg-gradient-to-r from-blue-500 to-purple-500 opacity-0 group-hover:opacity-100 transition-opacity duration-500"></div>
            </Card>
          ))}
        </div>

        {/* Enhanced Trust Indicators */}
        {/* <div ref={trustRef} className={`mt-20 ${createScrollAnimationClasses('fadeInUp', trustVisible)}`}>
          <div className="text-center mb-12">
            <div className="inline-flex items-center gap-2 bg-gradient-to-r from-gray-100 to-gray-200 px-6 py-3 rounded-full mb-6 border border-gray-200/50 backdrop-blur-sm">
              <Users size={16} className="text-gray-600" />
              <span className="text-gray-700 text-sm font-semibold">Proudly serving government agencies and municipalities</span>
            </div>
          </div>
          
          <div className="grid grid-cols-2 md:grid-cols-4 gap-8 max-w-4xl mx-auto">
            {[
              { name: "Ministry of Transportation", color: "blue" },
              { name: "Muscat Municipality", color: "green" },
              { name: "Royal Oman Police", color: "purple" },
              { name: "Salalah Municipality", color: "orange" }
            ].map((org, index) => (
              <div 
                key={org.name}
                className="group text-center p-6 rounded-2xl bg-white/60 backdrop-blur-sm border border-gray-200/50 hover:bg-white/80 hover:shadow-lg transition-all duration-300 hover:-translate-y-2"
              >
                <div className={`w-3 h-3 mx-auto mb-3 rounded-full bg-${org.color}-500 group-hover:scale-125 transition-transform duration-300`}></div>
                <div className="text-sm font-bold text-gray-700 group-hover:text-gray-900 transition-colors leading-tight">
                  {org.name}
                </div>
              </div>
            ))}
          </div>

          <div className="mt-16 grid grid-cols-1 md:grid-cols-3 gap-8 max-w-4xl mx-auto">
            <div className="text-center group">
              <div className="text-3xl lg:text-4xl font-bold text-blue-600 mb-2 group-hover:text-blue-700 transition-colors">15+</div>
              <div className="text-gray-600 font-semibold">Years of Excellence</div>
              <div className="text-sm text-gray-500 mt-1">Proven track record</div>
            </div>
            <div className="text-center group">
              <div className="text-3xl lg:text-4xl font-bold text-green-600 mb-2 group-hover:text-green-700 transition-colors">100%</div>
              <div className="text-gray-600 font-semibold">Client Satisfaction</div>
              <div className="text-sm text-gray-500 mt-1">Happy customers</div>
            </div>
            <div className="text-center group">
              <div className="text-3xl lg:text-4xl font-bold text-purple-600 mb-2 group-hover:text-purple-700 transition-colors">24/7</div>
              <div className="text-gray-600 font-semibold">Support Available</div>
              <div className="text-sm text-gray-500 mt-1">Always here for you</div>
            </div>
          </div>
        </div> */}
      </div>
    </section>
  );
};